import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import Heading, { Heading3Style, Heading4Style } from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useInterval from 'hooks/useInterval'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  LUX_PLUS_MONTH_LANDING_PAGE_URL,
  LUX_PLUS_MONTH_HOME_BANNER_IMAGE_URLS,
  LUX_PLUS_MONTH_BLOB_SHAPE_LARGE_RADIUS_PERCENT,
  LUX_PLUS_MONTH_BLOB_SHAPE_SMALL_RADIUS_PERCENT,
} from '../constants/luxPlusMonthTakeoverConstants'

const IMAGE_STATIC_DELAY_MS = 1300
const IMAGE_CROSSFADE_DURATION_MS = 1050
const IMAGE_ROTATION_INTERVAL_MS = IMAGE_STATIC_DELAY_MS + IMAGE_CROSSFADE_DURATION_MS

const BANNER_IMAGES = [
  {
    url: LUX_PLUS_MONTH_HOME_BANNER_IMAGE_URLS[0],
    mobilePosition: '35% 25%',
    mobileSize: 'auto 220%',
    position: '58% 25%',
    size: 'auto 220%',
  },
  {
    url: LUX_PLUS_MONTH_HOME_BANNER_IMAGE_URLS[1],
    mobilePosition: '40% 20%',
    mobileSize: 'auto 170%',
    position: '50% 20%',
    size: 'auto 170%',
  },
  {
    url: LUX_PLUS_MONTH_HOME_BANNER_IMAGE_URLS[2],
    mobilePosition: '58% 50%',
    mobileSize: 'auto 125%',
    position: '58% 65%',
    size: 'auto 125%',
  },
]

const StyledBanner = styled.div`
  position: relative;
  overflow: hidden;
  background-color: ${(props) => props.theme.palette.product.luxPlus.background};

  &.rounded-corners {
    ${mediaQueryUp.desktop} {
      border-radius: ${rem(16)};
    }
  }
`

const StyledContentContainer = styled(LayoutContainer)`
  position: relative;
`

const StyledContentGroup = styled(Group)`
  padding-block: ${rem(16)};
  padding-inline-start: ${rem(124)};
  min-height: ${rem(120)};

  ${mediaQueryUp.desktop} {
    padding-inline-start: ${rem(330)};
    padding-inline-end: ${rem(100)};
  }

  &.wide-desktop-inset {
    ${mediaQueryUp.desktop} {
      padding-inline-start: ${rem(390)};
      padding-inline-end: ${rem(20)};
    }
  }
`

const StyledCircleOverlay = styled.div`
  position: absolute;
  bottom: 0;
  top: ${rem(-36)};
  inset-inline-start: ${rem(-26)};
  width: ${rem(147)};
  height: ${rem(147)};
  overflow: hidden;
  border-start-start-radius: ${LUX_PLUS_MONTH_BLOB_SHAPE_LARGE_RADIUS_PERCENT};
  border-start-end-radius: ${LUX_PLUS_MONTH_BLOB_SHAPE_LARGE_RADIUS_PERCENT};
  border-end-start-radius: ${LUX_PLUS_MONTH_BLOB_SHAPE_LARGE_RADIUS_PERCENT};
  border-end-end-radius: ${LUX_PLUS_MONTH_BLOB_SHAPE_SMALL_RADIUS_PERCENT};
  background-color: ${(props) => props.theme.palette.product.luxPlus.background};

  ${mediaQueryUp.desktop} {
    top: ${rem(-41)};
    width: ${rem(294)};
    height: ${rem(294)};
    inset-inline-start: ${rem(16)};
  }

  &.wide-desktop-inset {
    ${mediaQueryUp.desktop} {
      inset-inline-start: ${rem(64)};
    }
  }
`

const StyledImageStack = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;

  ${mediaQueryUp.desktop} {
    height: 60%;
  }
`

function backgroundLayer(imageUrl: string, position: string, size: string) {
  return `url(${imageUrl}) ${position} / ${size} no-repeat`
}

const StyledImageLayer = styled.div<{
  $imageUrl: string,
  $mobilePosition: string,
  $mobileSize: string,
  $position: string,
  $size: string,
  $active: boolean,
}>`
  position: absolute;
  inset: 0;
  opacity: ${(props) => (props.$active ? 1 : 0)};
  transition: opacity ${IMAGE_CROSSFADE_DURATION_MS}ms ease-out;
  background: ${(props) => backgroundLayer(props.$imageUrl, props.$mobilePosition, props.$mobileSize)};

  ${mediaQueryUp.desktop} {
    background: ${(props) => backgroundLayer(props.$imageUrl, props.$position, props.$size)};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const StyledSubtitle = styled(Heading)`
  ${Heading4Style}

  ${mediaQueryUp.desktop} {
    ${Heading3Style}
  }
`

const StyledMobileOnly = styled.div`
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const StyledDesktopOnly = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: block;
  }
`

interface Props {
  roundedCorners?: boolean;
  wideDesktopInset?: boolean;
}

function LuxPlusMonthTakeoverHomeBanner(props: Props) {
  const { roundedCorners, wideDesktopInset } = props
  const intl = useIntl()
  const renderItalic = useCallback((chunks: ReactNode) => <i>{chunks}</i>, [])
  const [activeImageIndex, setActiveImageIndex] = useState(0)

  useInterval(() => {
    setActiveImageIndex((index) => (index + 1) % BANNER_IMAGES.length)
  }, IMAGE_ROTATION_INTERVAL_MS)

  const bannerImageAlt = intl.formatMessage({
    id: '2AGKULU8QertsaX9UdDk',
    defaultMessage: 'Boats on turquoise water beside limestone cliffs',
    description: 'Alternative text for the LuxPlus+ Month home page banner background image',
  })

  const viewAllOffersText = intl.formatMessage({
    id: 'gitTFnKD-K8n-tvpOMMC',
    defaultMessage: 'View all offers',
    description: 'Button text linking to all LuxPlus+ Month offers from the home page banner',
  })

  const renderEyebrowContent = (logoHeight: number) => <FormattedMessage
    id="K1ZhH_pUXvLorimqRMje"
    defaultMessage="Exclusive {luxPlusLogo} Member Event"
    description="Heading on the LuxPlus+ Month campaign banner shown above the home page hero carousel"
    values={{ luxPlusLogo: <LuxPlusLogo colour="inherit" height={logoHeight} inline /> }}
  />

  return <StyledBanner className={clsx({ 'rounded-corners': roundedCorners })}>
    <StyledContentContainer variant="large">
      <StyledCircleOverlay className={clsx({ 'wide-desktop-inset': wideDesktopInset })} role="img" aria-label={bannerImageAlt}>
        <StyledImageStack>
          {BANNER_IMAGES.map((image, index) => (
            <StyledImageLayer
              key={image.url}
              $imageUrl={image.url}
              $mobilePosition={image.mobilePosition}
              $mobileSize={image.mobileSize}
              $position={image.position}
              $size={image.size}
              $active={index === activeImageIndex}
            />
          ))}
        </StyledImageStack>
      </StyledCircleOverlay>
      <StyledContentGroup className={clsx({ 'wide-desktop-inset': wideDesktopInset })} direction="horizontal" gap={16} verticalAlign="center" desktopHorizontalAlign="space-between">
        <Group direction="vertical" gap={4}>
          <StyledMobileOnly>
            <BodyText variant="small" colour="static-white">
              {renderEyebrowContent(10)}
            </BodyText>
          </StyledMobileOnly>
          <StyledDesktopOnly>
            <BodyText variant="large" colour="static-white">
              {renderEyebrowContent(12)}
            </BodyText>
          </StyledDesktopOnly>
          <StyledSubtitle variant="heading3" colour="static-white">
            <FormattedMessage
              id="0bkRD7lEd0fnE8ymGIq5"
              defaultMessage="<i>Bigger savings</i> on unforgettable escapes"
              description="Subtitle on the LuxPlus+ Month campaign banner shown above the home page hero carousel"
              values={{ i: renderItalic }}
            />
          </StyledSubtitle>
          <StyledMobileOnly>
            <TextLink to={LUX_PLUS_MONTH_LANDING_PAGE_URL} colour="static-white" size="small" weight="regular">
              {viewAllOffersText}
            </TextLink>
          </StyledMobileOnly>
        </Group>
        <StyledDesktopOnly>
          <ProductPaletteProvider palette="lux-plus">
            <TextButton kind="primary" variant="ghost" size="medium" shape="round" to={LUX_PLUS_MONTH_LANDING_PAGE_URL}>
              {viewAllOffersText}
            </TextButton>
          </ProductPaletteProvider>
        </StyledDesktopOnly>
      </StyledContentGroup>
    </StyledContentContainer>
  </StyledBanner>
}

export default LuxPlusMonthTakeoverHomeBanner
